/**
 * MKS-393 — the "Personal details" and "Merch sizes" cards on /profile.
 *
 * Both read and write `GET`/`PATCH /api/auth/me/personal-details`, which is
 * self-scope only: the backend route carries `[authenticate]` and NOT
 * `impersonate`, so an admin acting as someone else sees and edits their OWN
 * details here. That is deliberate — an impersonator must not be able to
 * write somebody's PII.
 *
 * Visual language matches ProfilePage's other cards (`bg-white rounded-xl
 * shadow-sm …`), not the `ui/` kit PreferencesPage uses, and saving is an
 * explicit button rather than save-on-blur — the same choice
 * `PersonalEmailSection` made for the same reason.
 *
 * No birthday VISIBILITY control here, and no link to one. Birthdays are shown
 * to admins and HR only, so there is nothing for the person to decide: the card
 * states who can see the date and stops. It used to link to the
 * `Show my birthday to the team` switch on /profile/preferences and to say the
 * team dashboard shows the day and month — both wrong under that rule, and both
 * gone. That switch and its `showBirthdayToTeam` preference have since been
 * removed outright (MKS-392 follow-up, Petra's staging check 2026-11-02), so
 * there is no longer anywhere for this card to link to even if it wanted to.
 */
import { useEffect, useState } from 'react';
import { CakeIcon, GiftIcon } from '@heroicons/react/24/outline';
import { Button } from '../common/Button';
import { DateField } from '../ui/DateField';
import { MerchSizeFields } from './MerchSizeFields';
import { usePersonalDetails, useUpdatePersonalDetails } from '../../hooks/useMerchSizes';
import { useMerchSizeCharts } from '../../hooks/useMerchSizeCharts';
import type { PersonalDetails } from '../../services/api/merchSizes';
import type { MerchSizes } from '../../constants/merchSizes';

const EMPTY: PersonalDetails = {
  birthDate: null,
  merchTshirtSize: null,
  merchTshirtCut: null,
  merchHoodieSize: null,
  merchHoodieCut: null,
  merchSockSize: null,
  merchShoeSize: null,
};

const CARD_CLASS = 'bg-white rounded-xl shadow-sm border border-gray-200 p-6';

export function PersonalDetailsSection() {
  const { data, isLoading } = usePersonalDetails();
  const update = useUpdatePersonalDetails();
  const stored = data ?? EMPTY;

  const [birthDate, setBirthDate] = useState(stored.birthDate ?? '');
  // The query resolves after the first render, so adopt the server value once
  // it lands. Keyed on the stored value rather than on `isLoading` so a later
  // refetch that changes nothing does not stomp an in-progress edit.
  useEffect(() => {
    setBirthDate(stored.birthDate ?? '');
  }, [stored.birthDate]);

  const dirty = (stored.birthDate ?? '') !== birthDate;

  return (
    <div className={CARD_CLASS}>
      <div className="flex items-center gap-3 mb-4">
        <CakeIcon className="h-6 w-6 text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">Personal details</h2>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Your birth date. Visible only to you, to admins and to HR.
      </p>

      <div className="flex items-center gap-2">
        {/* MKS-351 — `DateField`, never a native `<input type="date">`: that
            one renders in the browser's locale and cannot be overridden.
            Same contract — ISO `YYYY-MM-DD` in and out, `''` for empty. */}
        <DateField
          aria-label="Birth date"
          value={birthDate}
          disabled={isLoading || update.isPending}
          onChange={setBirthDate}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <Button
          onClick={() => update.mutate({ birthDate: birthDate || null })}
          disabled={!dirty || update.isPending}
        >
          Save
        </Button>
        {stored.birthDate && (
          <Button
            variant="secondary"
            onClick={() => {
              setBirthDate('');
              update.mutate({ birthDate: null });
            }}
            disabled={update.isPending}
          >
            Clear
          </Button>
        )}
      </div>
    </div>
  );
}

export function MerchSizesSection() {
  const { data, isLoading } = usePersonalDetails();
  const update = useUpdatePersonalDetails();
  // MKS-393 size charts — which items HR has uploaded a chart for. This only
  // decides which "Size chart" links exist; an empty or failed answer simply
  // renders none, so it never blocks editing sizes.
  const { data: charts } = useMerchSizeCharts();
  const stored = data ?? EMPTY;

  const [draft, setDraft] = useState<MerchSizes>(stored);
  useEffect(() => {
    if (data) setDraft(data);
  }, [data]);

  const dirty = (Object.keys(EMPTY) as (keyof PersonalDetails)[])
    .filter((key) => key !== 'birthDate')
    .some((key) => draft[key as keyof MerchSizes] !== stored[key as keyof MerchSizes]);

  return (
    <div className={CARD_CLASS}>
      <div className="flex items-center gap-3 mb-4">
        <GiftIcon className="h-6 w-6 text-gray-400" />
        <h2 className="text-lg font-semibold text-gray-900">Merch sizes</h2>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        What we order for you when there is company swag. All optional — pick &ldquo;Not
        set&rdquo; to leave one out. Visible only to you, to admins and to HR.
      </p>

      <MerchSizeFields
        value={draft}
        disabled={isLoading || update.isPending}
        onChange={(patch) => setDraft((previous) => ({ ...previous, ...patch }))}
        charts={charts}
      />

      <div className="mt-4 flex items-center gap-2">
        <Button onClick={() => update.mutate(draft)} disabled={!dirty || update.isPending}>
          {update.isPending ? 'Saving...' : 'Save sizes'}
        </Button>
        {dirty && (
          <Button variant="secondary" onClick={() => setDraft(stored)} disabled={update.isPending}>
            Cancel
          </Button>
        )}
      </div>
    </div>
  );
}
